"use client"

import { useEffect, useMemo, useState } from "react"
import type { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"

import { DEFAULT_MARKET_CHANNEL, type MarketChannel } from "@/lib/market/market-channel"
import type { PdpContext } from "@/lib/storefront/product/pdp-service"

type StorefrontContextResponse = {
  isAnonymous?: boolean
  isB2B?: boolean
  isoLocale?: string
  userData?: PdpContext["userData"]
}

type ProductPricesResponse = {
  prices?: ProductPriceContainerCtnrROV1
  isAnonymous?: boolean
  isB2B?: boolean
  customerCategoryDiscount?: number
}

const contextPromiseCache = new Map<string, Promise<StorefrontContextResponse | undefined>>()
const pricesPromiseCache = new Map<string, Promise<ProductPricesResponse | undefined>>()

async function fetchJson<T>(url: string): Promise<T | undefined> {
  const response = await fetch(url, {
    cache: "no-store",
    credentials: "same-origin",
  })

  if (!response.ok) {
    return undefined
  }

  return (await response.json()) as T
}

function getContextPromise(sbLocale: string) {
  const cacheKey = sbLocale
  let promise = contextPromiseCache.get(cacheKey)

  if (!promise) {
    promise = fetchJson<StorefrontContextResponse>(`/api/storefront/context?sbLocale=${encodeURIComponent(sbLocale)}`)
    contextPromiseCache.set(cacheKey, promise)
  }

  return promise
}

export function getPdpPricesPromise(productId: string, sbLocale: string, channel: MarketChannel = DEFAULT_MARKET_CHANNEL) {
  const cacheKey = `${sbLocale}:${channel}:${productId}`
  let promise = pricesPromiseCache.get(cacheKey)

  if (!promise) {
    promise = fetchJson<ProductPricesResponse>(
      `/api/storefront/product-prices/${encodeURIComponent(productId)}?sbLocale=${encodeURIComponent(sbLocale)}&channel=${encodeURIComponent(channel)}`
    )
    pricesPromiseCache.set(cacheKey, promise)
  }

  return promise
}

function getPriceProductId(pdpContext: PdpContext) {
  const cheapestVariation = pdpContext.productAndArticle.masterProduct?.cheapestVariation

  if (pdpContext.productId.toUpperCase().startsWith("E") && cheapestVariation && cheapestVariation.length === 6) {
    return cheapestVariation
  }

  return pdpContext.productId
}

export function usePdpDynamicContext(pdpContext: PdpContext): PdpContext {
  const [storefrontContext, setStorefrontContext] = useState<StorefrontContextResponse | undefined>()
  const [prices, setPrices] = useState<ProductPriceContainerCtnrROV1 | undefined>(pdpContext.prices)
  const [customerCategoryDiscount, setCustomerCategoryDiscount] = useState<number | undefined>(
    pdpContext.customerCategoryDiscount
  )
  const [pricesLoaded, setPricesLoaded] = useState(!!pdpContext.prices)
  const priceProductId = getPriceProductId(pdpContext)

  useEffect(() => {
    let active = true

    getContextPromise(pdpContext.sbLocale)
      .then((result) => {
        if (active) {
          setStorefrontContext(result)
        }
      })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [pdpContext.sbLocale])

  useEffect(() => {
    if (pdpContext.prices) {
      setPricesLoaded(true)
      return
    }

    let active = true

    getPdpPricesPromise(priceProductId, pdpContext.sbLocale, pdpContext.channel ?? DEFAULT_MARKET_CHANNEL)
      .then((result) => {
        if (active) {
          setPrices(result?.prices)
          setCustomerCategoryDiscount(result?.customerCategoryDiscount)
          setStorefrontContext((current) => ({
            ...current,
            isAnonymous: result?.isAnonymous ?? current?.isAnonymous,
            isB2B: result?.isB2B ?? current?.isB2B,
          }))
          setPricesLoaded(true)
        }
      })
      .catch(() => {
        if (active) {
          setPricesLoaded(true)
        }
      })

    return () => {
      active = false
    }
  }, [pdpContext.prices, priceProductId])

  return useMemo(
    () => ({
      ...pdpContext,
      prices,
      customerCategoryDiscount: customerCategoryDiscount ?? pdpContext.customerCategoryDiscount,
      pricesLoading: !pricesLoaded && !prices,
      isAnonymous: storefrontContext?.isAnonymous ?? pdpContext.isAnonymous,
      isB2B: storefrontContext?.isB2B ?? pdpContext.isB2B,
      isoLocale: storefrontContext?.isoLocale ?? pdpContext.isoLocale,
      userData: storefrontContext?.userData ?? pdpContext.userData,
    }),
    [pdpContext, prices, pricesLoaded, customerCategoryDiscount, storefrontContext]
  )
}
